import {
  Component,
  OnInit,
  Input,
  ViewChild,
  ElementRef
} from '@angular/core';

@Component({
  selector: 'app-image-zoom-viewer',
  templateUrl: './image-zoom-viewer.component.html',
  styleUrls: ['./image-zoom-viewer.component.scss'],
})
export class ImageZoomViewerComponent {

  @Input() src!: string;
  @Input() alt = 'Zoomable image';
  @Input() maxZoom = 4;
  @Input() zoomStep = 0.5;

  @ViewChild('container', { static: true })
  containerRef!: ElementRef<HTMLDivElement>;

  scale = 1;
  posX = 0;
  posY = 0;

  private startX = 0;
  private startY = 0;
  private dragging = false;

  private lastTap = 0;
  private initialPinchDistance = 0;
  private initialScale = 1;
  private isPinching = false;

  get transform() {
    return `translate(${this.posX}px, ${this.posY}px) scale(${this.scale})`;
  }

  zoomIn() {
    this.scale = Math.min(this.scale + this.zoomStep, this.maxZoom);
    this.fixPositionAfterZoom();
  }

  zoomOut() {
    this.scale = Math.max(this.scale - this.zoomStep, 1);
    if (this.scale === 1) {
      this.reset();
    } else {
      this.fixPositionAfterZoom();
    }
  }

  startDrag(event: MouseEvent | TouchEvent) {
    if (this.scale <= 1 || this.isPinching) return;

    this.dragging = true;
    const p = this.getPoint(event);
    this.startX = p.x - this.posX;
    this.startY = p.y - this.posY;
  }

  onDrag(event: MouseEvent | TouchEvent) {
    if (!this.dragging) return;

    const p = this.getPoint(event);
    let newX = p.x - this.startX;
    let newY = p.y - this.startY;

    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const imgW = rect.width * this.scale;
    const imgH = rect.height * this.scale;

    const maxX = Math.max((imgW - rect.width) / 2, 0);
    const maxY = Math.max((imgH - rect.height) / 2, 0);

    this.posX = this.clamp(newX, -maxX, maxX);
    this.posY = this.clamp(newY, -maxY, maxY);
  }

  stopDrag() {
    this.dragging = false;
  }

  reset() {
    this.scale = 1;
    this.posX = 0;
    this.posY = 0;
  }

  private fixPositionAfterZoom() {
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const imgW = rect.width * this.scale;
    const imgH = rect.height * this.scale;

    const maxX = Math.max((imgW - rect.width) / 2, 0);
    const maxY = Math.max((imgH - rect.height) / 2, 0);

    this.posX = this.clamp(this.posX, -maxX, maxX);
    this.posY = this.clamp(this.posY, -maxY, maxY);
  }

  private clamp(val: number, min: number, max: number) {
    return Math.min(Math.max(val, min), max);
  }

  private getPoint(event: any) {
    if (event.touches?.length) {
      return {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY
      };
    }
    return { x: event.clientX, y: event.clientY };
  }

  onTouchStart(event: TouchEvent) {
    if (event.touches.length === 2) {
      // PINCH START
      this.isPinching = true;
      this.initialPinchDistance = this.getPinchDistance(event);
      this.initialScale = this.scale;
    } else if (event.touches.length === 1) {
      // DOUBLE TAP DETECTION
      const now = Date.now();
      if (now - this.lastTap < 300) {
        this.reset();
      }
      this.lastTap = now;
  
      this.startDrag(event);
    }
  }
  

  onTouchMove(event: TouchEvent) {
    if (this.isPinching && event.touches.length === 2) {
      const currentDistance = this.getPinchDistance(event);
      const scaleFactor = currentDistance / this.initialPinchDistance;
  
      this.scale = this.clamp(
        this.initialScale * scaleFactor,
        1,
        this.maxZoom
      );
  
      this.fixPositionAfterZoom();
    } else {
      this.onDrag(event);
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (event.touches.length < 2) {
      this.isPinching = false;
    }
    this.stopDrag();
  }
  
  private getPinchDistance(event: TouchEvent): number {
    const dx = event.touches[0].clientX - event.touches[1].clientX;
    const dy = event.touches[0].clientY - event.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
  
  

}
